import type { RoundName } from "../../domain/round/types";
import type { DpWorldGroup, DpWorldPreview, ScheduleStatus } from "./dpworldApi";

export type EmergencyScheduleOptions = {
  groupCount: number;
  twoTee: boolean;
  tee10Count: number;
  playersPerGroup: 2 | 3;
};

const CACHE_PREFIX = "golf-v2:tee-times:v1";

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, Math.round(value)));
}

function cacheKey(tournamentId: string, round: RoundName) {
  return `${CACHE_PREFIX}:${tournamentId || "unknown"}:${round}`;
}

export function loadCachedPreview(tournamentId: string, round: RoundName): DpWorldPreview | null {
  try {
    const raw = window.localStorage.getItem(cacheKey(tournamentId, round));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DpWorldPreview;
    if (!parsed || parsed.roundName !== round || !Array.isArray(parsed.groups)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveCachedPreview(tournamentId: string, round: RoundName, preview: DpWorldPreview) {
  try {
    window.localStorage.setItem(cacheKey(tournamentId, round), JSON.stringify({ ...preview, cachedAt: new Date().toISOString() }));
  } catch {
    // Local cache is an operational convenience; a full/blocked storage must not break the generator.
  }
}

export function clearCachedPreview(tournamentId: string, round: RoundName) {
  try {
    window.localStorage.removeItem(cacheKey(tournamentId, round));
  } catch {
    // Ignore browser storage errors.
  }
}

export function groupScheduleStatus(group: DpWorldGroup): ScheduleStatus {
  const explicit = String(group.scheduleStatus || "").toLowerCase();
  if (["scheduled", "pending", "postponed", "suspended", "completed"].includes(explicit)) {
    return explicit as ScheduleStatus;
  }
  const start = String(group.startTime || group.start || group.time || "").trim();
  return !start || start === "00:00" || start === "0:00" ? "postponed" : "scheduled";
}

export function hasUnofficialTiming(groups: Array<Record<string, unknown>>) {
  return groups.some((raw) => {
    const group = raw as DpWorldGroup;
    return group.timingSource === "emergency" || ["pending", "postponed"].includes(groupScheduleStatus(group));
  });
}

export function buildEmergencyPreview(
  roundName: RoundName,
  options: EmergencyScheduleOptions,
): DpWorldPreview {
  const groupCount = clamp(options.groupCount, 1, 80);
  const twoTee = Boolean(options.twoTee && groupCount > 1);
  const tee10Count = twoTee ? clamp(options.tee10Count, 1, groupCount - 1) : 0;
  const playersPerGroup = options.playersPerGroup === 2 ? 2 : 3;
  const groups: DpWorldGroup[] = [];

  for (let groupNumber = 1; groupNumber <= groupCount; groupNumber += 1) {
    const tee = twoTee && groupNumber <= tee10Count ? 10 : 1;
    groups.push({
      group: groupNumber,
      tee,
      start: "",
      startTime: "",
      time: "",
      finish9: "",
      finish18: "",
      firstNineLabel: tee === 1 ? "1-9" : "10-18",
      secondNineLabel: tee === 1 ? "10-18" : "1-9",
      session: "pending",
      players: Array.from({ length: playersPerGroup }, () => ({ name: "", country: "UNK" })),
      tv: false,
      requiredScorers: 1,
      scheduleStatus: "pending",
      timingSource: "emergency",
    });
  }

  const tee1Count = groups.filter((group) => Number(group.tee) === 1).length;
  const actualTee10Count = groups.filter((group) => Number(group.tee) === 10).length;
  return {
    roundName,
    sourceUrl: "emergency://manual-skeleton",
    rawText: "",
    groups,
    count: groups.length,
    emergency: true,
    cachedAt: new Date().toISOString(),
    diagnostics: {
      complete: true,
      groupCount: groups.length,
      expectedCount: groups.length,
      playerGroups: groups.length,
      playerCount: groups.length * playersPerGroup,
      missingPlayerGroups: [],
      teeCounts: { "1": tee1Count, "10": actualTee10Count },
      sessionCounts: { pending: groups.length },
      statusCounts: { pending: groups.length },
      scheduledCount: 0,
      postponedCount: groups.length,
      duplicateSlots: [],
      duplicateGroups: [],
      malformedTimeGroups: [],
      sourceMode: "tryb awaryjny",
      parserVersion: "manual-skeleton-v1",
      warnings: ["Plan nie ma jeszcze oficjalnych tee times. Transport pozostaje wyłączony do czasu uzupełnienia godzin."],
    },
  };
}

function hhmmToMinutes(value: string) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
}

function minutesToHhmm(value: number) {
  const normalized = ((Math.round(value) % 1440) + 1440) % 1440;
  return `${String(Math.floor(normalized / 60)).padStart(2, "0")}:${String(normalized % 60).padStart(2, "0")}`;
}

/**
 * The legacy solver needs a clock time. Emergency schedules intentionally do
 * not expose one to the operator, so only the worker payload receives these
 * deterministic technical slots. They are NOT official tee times.
 */
export function materializeTechnicalTimes<T extends Record<string, unknown>>(groups: T[]): T[] {
  const perTeeIndex = new Map<number, number>();
  return groups.map((raw) => {
    const group = raw as T & DpWorldGroup;
    const status = groupScheduleStatus(group);
    const existing = String(group.startTime || group.start || group.time || "").trim();
    if (existing && existing !== "00:00" && !["pending", "postponed"].includes(status)) return raw;

    const tee = Number(group.tee || 1) === 10 ? 10 : 1;
    const index = perTeeIndex.get(tee) || 0;
    perTeeIndex.set(tee, index + 1);
    const startMinutes = 8 * 60 + index * 10;
    const playerCount = Array.isArray(group.players) ? group.players.length : 3;
    const finish9Minutes = playerCount === 2 ? 120 : 150;
    const finish18Minutes = playerCount === 2 ? 240 : 300;
    const start = minutesToHhmm(startMinutes);
    return {
      ...raw,
      time: start,
      startTime: start,
      start,
      finish9: minutesToHhmm(startMinutes + finish9Minutes),
      finish18: minutesToHhmm(startMinutes + finish18Minutes),
      timingSynthetic: true,
      scheduleStatus: status,
    };
  });
}

export type SuspendedPosition = "tee" | "fairway" | "green" | "finished";

export function estimateReleaseTime(args: {
  restartTime: string;
  tee: number;
  hole: number;
  position: SuspendedPosition;
  paceMinutesPerHole: number;
  releaseBufferMinutes: number;
}) {
  const restart = hhmmToMinutes(args.restartTime);
  if (restart === null) return "";
  const tee = args.tee === 10 ? 10 : 1;
  const hole = clamp(args.hole, 1, 18);
  const sequence = tee === 10
    ? [10, 11, 12, 13, 14, 15, 16, 17, 18, 1, 2, 3, 4, 5, 6, 7, 8, 9]
    : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];
  const index = Math.max(0, sequence.indexOf(hole));
  const afterCurrent = Math.max(0, sequence.length - index - 1);
  const currentFraction = args.position === "finished" ? 0 : args.position === "green" ? 0.25 : args.position === "fairway" ? 0.6 : 1;
  const remainingEquivalentHoles = afterCurrent + currentFraction;
  return minutesToHhmm(restart + remainingEquivalentHoles * Math.max(1, args.paceMinutesPerHole) + Math.max(0, args.releaseBufferMinutes));
}
